'use client';

import {
  createContext,
  useContext,
  useEffect,
  useReducer,
  useRef,
  type ReactNode,
} from 'react';

import { useComments } from '@/hooks/useComments';
import { useSession } from '@/hooks/useSession';
import { commentToPin } from '@/lib/commentToPin';
import { isUnresolvedName } from '@/utils/translateDisplayName';

import type { CommentStatus } from '@/services/comments/types';
import type {
  CommentReply,
  CommentUserInfo,
  InitializeData,
  Pin,
} from '@/types/feedback';

interface CommentsState {
  pins: Pin[];
  userInfo: CommentUserInfo | null;
  visitorId: string | null;
  isLoaded: boolean;
  commenterInfoMap: Record<string, CommentUserInfo>;
}

export type CommentsAction =
  | {
      type: 'LOAD_COMMENTS';
      payload: {
        pins: Pin[];
        userInfo: CommentUserInfo | undefined;
        visitorId: string | undefined;
        commenterInfoMap?: Record<string, CommentUserInfo>;
      };
    }
  | { type: 'ADD_PIN'; payload: Pin }
  | { type: 'DELETE_PIN'; payload: { id: string } }
  | { type: 'UPDATE_PIN_LABEL'; payload: { id: string; label: string } }
  | {
      type: 'MOVE_PIN';
      payload: {
        id: string;
        selector: string;
        offsetXPct: number;
        offsetYPct: number;
        fallbackXPct: number;
        fallbackYPct: number;
      };
    }
  | {
      type: 'COMMENT_PERSISTED';
      payload: { pinId: string; commentId: string; revision: string | null };
    }
  | { type: 'UPDATE_USER_INFO'; payload: CommentUserInfo }
  | { type: 'ADD_REPLY'; payload: { pinId: string; reply: CommentReply } }
  | { type: 'REMOVE_REPLY'; payload: { pinId: string; replyIndex: number } }
  | { type: 'REMOVE_LOCAL_REPLY'; payload: { pinId: string; localId: number } }
  | {
      type: 'REPLY_PERSISTED';
      payload: { pinId: string; revision: string | null };
    }
  | {
      type: 'UPDATE_PIN_REPLIES';
      payload: {
        pinId: string;
        replies: CommentReply[];
        revision: string | null;
      };
    }
  | {
      type: 'UPDATE_PIN_STATUS';
      payload: { id: string; status: CommentStatus; revision: string | null };
    };

const initialState: CommentsState = {
  pins: [],
  userInfo: null,
  visitorId: null,
  isLoaded: false,
  commenterInfoMap: {},
};

/**
 * The name map and the viewer's identity arrive in separate payloads
 * (`api/comments` / SSR vs `api/session`), so the map can end up without an
 * entry for the viewer's own id — their replies then have no name to resolve.
 * Mirror the session info under that id, but never replace an already-resolved
 * entry with an unresolved fallback (the partner's visitor lookup returns one).
 */
function withSelfInfo(
  commenterInfoMap: Record<string, CommentUserInfo>,
  visitorId: string | null | undefined,
  userInfo: CommentUserInfo | null | undefined,
): Record<string, CommentUserInfo> {
  if (!visitorId || !userInfo) return commenterInfoMap;
  const existing = commenterInfoMap[visitorId];
  if (existing && isUnresolvedName(userInfo.userName)) {
    return commenterInfoMap;
  }
  return { ...commenterInfoMap, [visitorId]: userInfo };
}

function initialDataToState(data: InitializeData): CommentsState {
  return {
    pins: data.comments.map(commentToPin),
    userInfo: data.userInfo ?? null,
    visitorId: data.visitorId ?? null,
    isLoaded: true,
    commenterInfoMap: withSelfInfo(
      data.commenterInfoMap ?? {},
      data.visitorId,
      data.userInfo,
    ),
  };
}

function commentsReducer(
  state: CommentsState,
  action: CommentsAction,
): CommentsState {
  switch (action.type) {
    case 'LOAD_COMMENTS':
      return {
        pins: action.payload.pins,
        userInfo: action.payload.userInfo ?? null,
        visitorId: action.payload.visitorId ?? null,
        isLoaded: true,
        commenterInfoMap: withSelfInfo(
          action.payload.commenterInfoMap ?? {},
          action.payload.visitorId,
          action.payload.userInfo,
        ),
      };
    case 'ADD_PIN':
      return { ...state, pins: [...state.pins, action.payload] };
    case 'DELETE_PIN':
      return {
        ...state,
        pins: state.pins.filter((p) => p.id !== action.payload.id),
      };
    case 'UPDATE_PIN_LABEL':
      return {
        ...state,
        pins: state.pins.map((p) =>
          p.id === action.payload.id
            ? { ...p, label: action.payload.label }
            : p,
        ),
      };
    case 'MOVE_PIN': {
      const {
        id,
        selector,
        offsetXPct,
        offsetYPct,
        fallbackXPct,
        fallbackYPct,
      } = action.payload;
      return {
        ...state,
        pins: state.pins.map((p) =>
          p.id === id
            ? {
                ...p,
                // A human reposition anchors the pin to the dropped element and
                // clears legacy status, so the disclaimer disappears immediately.
                selector,
                offsetXPct,
                offsetYPct,
                fallbackXPct,
                fallbackYPct,
                isLegacy: false,
                legacyX: undefined,
                legacyY: undefined,
              }
            : p,
        ),
      };
    }
    case 'COMMENT_PERSISTED':
      return {
        ...state,
        pins: state.pins.map((p) =>
          p.id === action.payload.pinId
            ? {
                ...p,
                commentId: action.payload.commentId,
                revision: action.payload.revision,
              }
            : p,
        ),
      };
    case 'UPDATE_USER_INFO':
      return {
        ...state,
        userInfo: action.payload,
        commenterInfoMap: state.visitorId
          ? { ...state.commenterInfoMap, [state.visitorId]: action.payload }
          : state.commenterInfoMap,
      };
    case 'ADD_REPLY': {
      const { pinId, reply } = action.payload;
      return {
        ...state,
        pins: state.pins.map((p) =>
          p.id === pinId ? { ...p, replies: [...(p.replies ?? []), reply] } : p,
        ),
      };
    }
    case 'REMOVE_REPLY':
      return {
        ...state,
        pins: state.pins.map((p) =>
          p.id === action.payload.pinId
            ? {
                ...p,
                replies: (p.replies ?? []).filter(
                  (_r, i) => i !== action.payload.replyIndex,
                ),
              }
            : p,
        ),
      };
    case 'REMOVE_LOCAL_REPLY':
      return {
        ...state,
        pins: state.pins.map((p) =>
          p.id === action.payload.pinId
            ? {
                ...p,
                replies: (p.replies ?? []).filter(
                  (r) => r.localId !== action.payload.localId,
                ),
              }
            : p,
        ),
      };
    case 'REPLY_PERSISTED':
      return {
        ...state,
        pins: state.pins.map((p) =>
          p.id === action.payload.pinId
            ? { ...p, revision: action.payload.revision }
            : p,
        ),
      };
    case 'UPDATE_PIN_REPLIES':
      return {
        ...state,
        pins: state.pins.map((p) =>
          p.id === action.payload.pinId
            ? {
                ...p,
                replies: action.payload.replies,
                revision: action.payload.revision,
              }
            : p,
        ),
      };
    case 'UPDATE_PIN_STATUS':
      return {
        ...state,
        pins: state.pins.map((p) =>
          p.id === action.payload.id
            ? {
                ...p,
                status: action.payload.status,
                revision: action.payload.revision,
              }
            : p,
        ),
      };
    default:
      return state;
  }
}

interface CommentsContextValue {
  state: CommentsState;
  dispatch: React.Dispatch<CommentsAction>;
}

const CommentsContext = createContext<CommentsContextValue | null>(null);

interface CommentsProviderProps {
  sealedContext?: string;
  initialData?: InitializeData;
  children: ReactNode;
}

export function CommentsProvider({
  sealedContext,
  initialData,
  children,
}: CommentsProviderProps) {
  const [state, dispatch] = useReducer(commentsReducer, initialData, (data) =>
    data ? initialDataToState(data) : initialState,
  );

  const hasInitialData = Boolean(initialData);

  // Fetch only when not pre-loaded via initialData. Partner mode gets both
  // comments and session from /partner/proxy, so it never calls these routes.
  const { data: sessionData } = useSession({
    sealedContext,
    enabled: !hasInitialData,
  });
  const { data: commentsData } = useComments({
    sealedContext,
    enabled: !hasInitialData,
  });

  // Non-owner mode: wait for both session and comments before dispatching
  const commentsDispatchedRef = useRef(hasInitialData);
  useEffect(() => {
    if (
      hasInitialData ||
      !commentsData ||
      !sessionData ||
      commentsDispatchedRef.current
    )
      return;
    commentsDispatchedRef.current = true;
    dispatch({
      type: 'LOAD_COMMENTS',
      payload: {
        pins: commentsData.comments.map(commentToPin),
        visitorId: sessionData.visitorId,
        userInfo: sessionData.userInfo,
        commenterInfoMap: commentsData.commenterInfoMap,
      },
    });
  }, [hasInitialData, commentsData, sessionData]);

  return (
    <CommentsContext.Provider value={{ state, dispatch }}>
      {children}
    </CommentsContext.Provider>
  );
}

export function useCommentsContext() {
  const context = useContext(CommentsContext);
  if (!context) {
    throw new Error(
      'useCommentsContext must be used within a CommentsProvider',
    );
  }
  return context;
}
